import axios from "axios";
import React from "react";
import { getRequestHeaders } from "../api";
import { baseUrl } from "../assets/Animations";
import "./josh-lesson-confirmation-test.css";

export const JOSH_E2E_CONFIRMATION =
  "SEND_JOSH_LESSON_CONFIRMATION_TEST";

const JOSH_E2E_ENDPOINT =
  "/native/lesson-confirmations/admin/josh-e2e-test";

const SAFETY_ERROR_HELP = {
  JOSH_TEST_DEVICE_NOT_READY:
    "Josh's enrollment, app installation, notification permission, and active device token must all be ready before this test can run.",
  JOSH_E2E_REAL_LESSONS_EXIST:
    "The selected date contains real Josh calendar lessons. Choose a completely empty date so the test stays isolated.",
  JOSH_E2E_TEST_ALREADY_SUBMITTED:
    "That demo confirmation day has already been submitted. Choose another empty date.",
  JOSH_E2E_NOTHING_TO_CONFIRM:
    "That demo day has no unanswered lessons. Choose another empty date.",
};

const redactSensitiveText = (value) =>
  String(value || "")
    .replace(/(?:Exponent|Expo)PushToken\[[^\]]+\]/gi, "[redacted push token]")
    .replace(
      /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g,
      "[redacted JWT]"
    )
    .slice(0, 1000);

const safeText = (value) => {
  if (typeof value !== "string" && typeof value !== "number") return "";
  return redactSensitiveText(value);
};

const safeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const safeErrorText = (value) => {
  if (typeof value === "string" || typeof value === "number") {
    return safeText(value);
  }
  if (!value || typeof value !== "object") return "";
  return [value.code, value.message, value.error]
    .map(safeText)
    .filter(Boolean)
    .join(" · ");
};

/**
 * Keep only fields approved for this internal status view. In particular, do
 * not retain arbitrary API fields that could contain an auth credential or a
 * raw Expo token.
 */
export const sanitizeJoshE2EState = (payload = {}) => {
  const target = payload?.target || {};
  const teacher = target?.teacher || {};
  const eligibility = target?.eligibility || {};
  const installation = eligibility?.installation || null;
  const notification = payload?.notification || null;
  const batch = payload?.batch || null;
  const enabledFeatures = Array.isArray(eligibility?.enabledFeatures)
    ? eligibility.enabledFeatures.map(safeText).filter(Boolean)
    : [];
  const deliveries = Array.isArray(payload?.deliveries)
    ? payload.deliveries.map((delivery) => ({
        status: safeText(delivery?.status),
        attempts: safeNumber(delivery?.attempts),
        deliveredAt: safeText(delivery?.deliveredAt),
        receiptCheckedAt: safeText(delivery?.receiptCheckedAt),
        error: safeErrorText(delivery?.error),
      }))
    : [];
  const demoLessonCount = Array.isArray(payload?.items)
    ? payload.items.filter((item) => item?.isDemo === true).length
    : 0;

  return {
    serviceDate: safeText(payload?.serviceDate),
    schedulerChanged: payload?.schedulerChanged === true,
    target: {
      teacher: {
        name: safeText(teacher?.name),
        teacherId: safeText(teacher?.teacherId),
      },
      eligibility: {
        eligible: eligibility?.eligible === true,
        enrollmentStatus: safeText(eligibility?.enrollmentStatus),
        enabledFeatures,
        hasRecentInstallation: eligibility?.hasRecentInstallation === true,
        installation: installation
          ? {
              platform: safeText(installation?.platform),
              appVersion: safeText(installation?.appVersion),
              buildVersion: safeText(installation?.buildVersion),
              notificationPermission: safeText(
                installation?.notificationPermission
              ),
              lastSeenAt: safeText(installation?.lastSeenAt),
            }
          : null,
        activePushTokenCount: safeNumber(
          eligibility?.activePushTokenCount
        ),
      },
    },
    batch: batch
      ? {
          status: safeText(batch?.status),
          lessonCount: safeNumber(batch?.lessonCount),
          pendingCount: safeNumber(batch?.pendingCount),
          submittedAt: safeText(batch?.submittedAt),
          pushState: safeText(batch?.pushState),
          pushSentAt: safeText(batch?.pushSentAt),
          pushLastError: safeErrorText(batch?.pushLastError),
          notificationId: safeText(batch?.notificationId),
          isDemo: batch?.isDemo === true,
        }
      : null,
    notification: notification
      ? {
          id: safeText(notification?.id),
          status: safeText(notification?.status),
          sentAt: safeText(notification?.sentAt),
          openedAt: safeText(notification?.openedAt),
          actionedAt: safeText(notification?.actionedAt),
          action: safeText(notification?.action),
          sendAttempts: safeNumber(notification?.sendAttempts),
          lastError: safeErrorText(notification?.lastError),
        }
      : null,
    deliveries,
    demoLessonCount,
  };
};

export const normalizeJoshE2EError = (requestError) => {
  const response = requestError?.response;
  const code = safeText(response?.data?.code) || "JOSH_E2E_REQUEST_FAILED";
  const message =
    safeText(response?.data?.error) ||
    safeText(response?.data?.message) ||
    safeText(requestError?.message) ||
    "The Josh lesson-confirmation test request could not be completed.";

  return {
    status: safeNumber(response?.status),
    code,
    message,
    help: SAFETY_ERROR_HELP[code] || "",
  };
};

const formatDateTime = (value) => {
  if (!value) return "Not recorded";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? safeText(value)
    : parsed.toLocaleString();
};

const formatFeature = (feature) =>
  safeText(feature)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const displayValue = (value, fallback = "Not reported") =>
  value === null || value === undefined || value === "" ? fallback : value;

const StatusPill = ({ good, children }) => (
  <span
    className={`josh-e2e__pill ${
      good ? "josh-e2e__pill--good" : "josh-e2e__pill--warning"
    }`}
  >
    {children}
  </span>
);

const Detail = ({ label, children, wide = false }) => (
  <div className={`josh-e2e__detail ${wide ? "josh-e2e__detail--wide" : ""}`}>
    <dt>{label}</dt>
    <dd>{displayValue(children)}</dd>
  </div>
);

const RequestError = ({ error }) => {
  if (!error) return null;
  const isSafetyBlock = error.status === 409;
  return (
    <section
      className={`josh-e2e__request-error ${
        isSafetyBlock ? "josh-e2e__request-error--safety" : ""
      }`}
      role="alert"
    >
      <div className="josh-e2e__request-error-heading">
        {isSafetyBlock ? "Safety block" : "Request error"}
        {error.status ? ` · HTTP ${error.status}` : ""}
      </div>
      <code>{error.code}</code>
      <p>{error.message}</p>
      {error.help && <p className="josh-e2e__request-error-help">{error.help}</p>}
    </section>
  );
};

const Readiness = ({ testState }) => {
  const { teacher, eligibility } = testState.target;
  const installation = eligibility.installation;
  const installationDetail = installation
    ? [
        installation.platform,
        installation.appVersion && `app ${installation.appVersion}`,
        installation.buildVersion && `build ${installation.buildVersion}`,
        installation.lastSeenAt && `last seen ${formatDateTime(installation.lastSeenAt)}`,
      ]
        .filter(Boolean)
        .join(" · ")
    : "No recent installation reported";

  return (
    <section className="josh-e2e__card" aria-labelledby="josh-readiness-heading">
      <div className="josh-e2e__section-heading">
        <div>
          <p className="josh-e2e__eyebrow">Read-only preflight</p>
          <h2 id="josh-readiness-heading">Readiness</h2>
        </div>
        <StatusPill good={eligibility.eligible}>
          {eligibility.eligible ? "Ready to test" : "Device not ready"}
        </StatusPill>
      </div>

      <dl className="josh-e2e__details-grid">
        <Detail label="Teacher">{teacher.name}</Detail>
        <Detail label="Teacher ID">{teacher.teacherId}</Detail>
        <Detail label="Enrollment status">{eligibility.enrollmentStatus}</Detail>
        <Detail label="Active push-token count">
          {eligibility.activePushTokenCount}
        </Detail>
        <Detail label="Recent app installation">
          {eligibility.hasRecentInstallation ? "Yes" : "No"}
        </Detail>
        <Detail label="Notification permission">
          {installation?.notificationPermission}
        </Detail>
        <Detail label="Enabled native features" wide>
          {eligibility.enabledFeatures.length
            ? eligibility.enabledFeatures.map(formatFeature).join(", ")
            : "None reported"}
        </Detail>
        <Detail label="Installation details" wide>
          {installationDetail}
        </Detail>
      </dl>
    </section>
  );
};

const TestResults = ({ testState }) => {
  const notification = testState.notification;
  const batch = testState.batch;
  const hasTestState = Boolean(notification || batch);
  const notificationStatus = notification?.status || batch?.pushState;
  const notificationId = notification?.id || batch?.notificationId;
  const sentAt = notification?.sentAt || batch?.pushSentAt;

  return (
    <section className="josh-e2e__card" aria-labelledby="josh-result-heading">
      <div className="josh-e2e__section-heading">
        <div>
          <p className="josh-e2e__eyebrow">Selected date</p>
          <h2 id="josh-result-heading">Test status</h2>
        </div>
        {hasTestState && (
          <StatusPill good={notificationStatus === "sent"}>
            {notificationStatus || batch?.status || "Created"}
          </StatusPill>
        )}
      </div>

      {!hasTestState ? (
        <p className="josh-e2e__empty-state">
          No isolated Josh demo test exists for this date.
        </p>
      ) : (
        <>
          <dl className="josh-e2e__details-grid">
            {notificationStatus && (
              <Detail label="Notification status">{notificationStatus}</Detail>
            )}
            {notificationId && (
              <Detail label="Notification ID">{notificationId}</Detail>
            )}
            {sentAt && (
              <Detail label="Sent timestamp">{formatDateTime(sentAt)}</Detail>
            )}
            {notification && (
              <>
                <Detail label="Opened timestamp">
                  {formatDateTime(notification.openedAt)}
                </Detail>
                <Detail label="Actioned timestamp">
                  {formatDateTime(notification.actionedAt)}
                </Detail>
                <Detail label="Action">{notification.action}</Detail>
                <Detail label="Notification send attempts">
                  {notification.sendAttempts}
                </Detail>
                {notification.lastError && (
                  <Detail label="Notification error" wide>
                    {notification.lastError}
                  </Detail>
                )}
              </>
            )}

            {batch && (
              <>
                <Detail label="Batch status">{batch.status}</Detail>
                <Detail label="Push state">{batch.pushState}</Detail>
                <Detail label="Pending lesson count">{batch.pendingCount}</Detail>
                <Detail label="Submitted timestamp">
                  {formatDateTime(batch.submittedAt)}
                </Detail>
                {batch.pushLastError && (
                  <Detail label="Batch delivery error" wide>
                    {batch.pushLastError}
                  </Detail>
                )}
              </>
            )}
          </dl>

          <div className="josh-e2e__delivery-list" aria-label="Delivery status">
            <h3>Delivery</h3>
            {testState.deliveries.length ? (
              testState.deliveries.map((delivery, index) => (
                <div className="josh-e2e__delivery" key={`delivery-${index}`}>
                  <div>
                    <strong>Attempt {index + 1}</strong>
                    <span>{displayValue(delivery.status)}</span>
                  </div>
                  <div>
                    Attempts: {displayValue(delivery.attempts, "0")}
                    {delivery.deliveredAt
                      ? ` · Delivered ${formatDateTime(delivery.deliveredAt)}`
                      : ""}
                  </div>
                  {delivery.error && (
                    <p className="josh-e2e__delivery-error">{delivery.error}</p>
                  )}
                </div>
              ))
            ) : (
              <p className="josh-e2e__empty-state">
                No delivery attempt has been recorded yet.
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
};

const ConfirmationModal = ({ serviceDate, busy, onCancel, onConfirm }) => {
  React.useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [busy, onCancel]);

  return (
    <div className="josh-e2e__modal-backdrop">
      <div
        className="josh-e2e__modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="josh-send-confirmation-title"
        aria-describedby="josh-send-confirmation-description"
      >
        <div className="josh-e2e__modal-icon" aria-hidden="true">
          !
        </div>
        <h2 id="josh-send-confirmation-title">Send to Josh's real phone?</h2>
        <p id="josh-send-confirmation-description">
          This will send a real push notification to Josh Graham's enrolled
          phone for <strong>{serviceDate}</strong>. Josh must open the notification
          and complete the lesson-confirmation flow himself.
        </p>
        <p className="josh-e2e__modal-safety">
          Confirm that this date contains no real Josh calendar lessons.
        </p>
        <div className="josh-e2e__modal-actions">
          <button type="button" className="josh-e2e__button josh-e2e__button--secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="josh-e2e__button josh-e2e__button--danger" onClick={onConfirm} disabled={busy}>
            {busy ? "Sending…" : "Yes, Send to Josh's Real Phone"}
          </button>
        </div>
      </div>
    </div>
  );
};

const JoshLessonConfirmationTest = ({ userData }) => {
  const [serviceDate, setServiceDate] = React.useState("");
  const [testState, setTestState] = React.useState(null);
  const [checkedDate, setCheckedDate] = React.useState("");
  const [requestError, setRequestError] = React.useState(null);
  const [busyAction, setBusyAction] = React.useState("");
  const [confirmationOpen, setConfirmationOpen] = React.useState(false);
  const [sendAttempted, setSendAttempted] = React.useState(false);
  const requestInFlight = React.useRef(false);

  const endpoint = serviceDate
    ? `${baseUrl}${JOSH_E2E_ENDPOINT}/${encodeURIComponent(serviceDate)}`
    : "";

  const loadStatus = async () => {
    if (!serviceDate || requestInFlight.current) return;
    requestInFlight.current = true;
    setBusyAction("status");
    setRequestError(null);
    try {
      const response = await axios.get(endpoint, getRequestHeaders());
      setTestState(sanitizeJoshE2EState(response.data));
      setCheckedDate(serviceDate);
    } catch (error) {
      setTestState(null);
      setCheckedDate("");
      setRequestError(normalizeJoshE2EError(error));
    } finally {
      requestInFlight.current = false;
      setBusyAction("");
    }
  };

  const sendTest = async () => {
    if (
      !serviceDate ||
      checkedDate !== serviceDate ||
      requestInFlight.current
    ) {
      return;
    }
    requestInFlight.current = true;
    setBusyAction("send");
    setSendAttempted(true);
    setRequestError(null);
    try {
      const response = await axios.post(
        endpoint,
        { confirmation: JOSH_E2E_CONFIRMATION },
        getRequestHeaders()
      );
      setTestState(sanitizeJoshE2EState(response.data));
      setCheckedDate(serviceDate);
    } catch (error) {
      setRequestError(normalizeJoshE2EError(error));
    } finally {
      requestInFlight.current = false;
      setBusyAction("");
      setConfirmationOpen(false);
    }
  };

  if (!userData?.IsAdmin) return null;

  const readinessCurrent = Boolean(testState && checkedDate === serviceDate);
  const deviceReady = testState?.target?.eligibility?.eligible === true;
  const notificationSent = Boolean(
    testState?.notification?.sentAt ||
      testState?.notification?.status === "sent" ||
      testState?.batch?.pushSentAt ||
      testState?.batch?.pushState === "sent"
  );
  const sendDisabled =
    !readinessCurrent || !deviceReady || Boolean(busyAction) || notificationSent;

  const changeDate = (event) => {
    setServiceDate(event.target.value);
    setTestState(null);
    setCheckedDate("");
    setRequestError(null);
    setConfirmationOpen(false);
    setSendAttempted(false);
  };

  return (
    <main className="josh-e2e" aria-labelledby="josh-e2e-title">
      <header className="josh-e2e__header">
        <div>
          <p className="josh-e2e__eyebrow">Internal admin tool</p>
          <h1 id="josh-e2e-title">Josh Lesson Confirmation Test</h1>
          <p>
            Run the isolated native-app lesson-confirmation flow against the
            server-fixed Josh Graham test target.
          </p>
        </div>
        <span className="josh-e2e__admin-badge">Administrator only</span>
      </header>

      <section className="josh-e2e__safety-notice" aria-label="Test safety notice">
        <div className="josh-e2e__safety-icon" aria-hidden="true">!</div>
        <div>
          <h2>Choose an empty Josh calendar date</h2>
          <p>
            The selected date must not contain any real Josh calendar lessons.
            The server will block the test if real lessons exist.
          </p>
          <p>
            <strong>All generated lessons are demo data and excluded from payroll.</strong>
            {" "}This page does not change notification scheduling or submit lesson answers.
          </p>
        </div>
      </section>

      <section className="josh-e2e__controls" aria-label="Josh test controls">
        <label htmlFor="josh-e2e-service-date">
          Service date
          <input
            id="josh-e2e-service-date"
            type="date"
            value={serviceDate}
            onChange={changeDate}
            disabled={Boolean(busyAction)}
          />
        </label>
        <button
          type="button"
          className="josh-e2e__button josh-e2e__button--primary"
          onClick={loadStatus}
          disabled={!serviceDate || Boolean(busyAction)}
        >
          {busyAction === "status" && !sendAttempted
            ? "Checking…"
            : "Check Readiness"}
        </button>
        {(sendAttempted || testState?.batch || testState?.notification) && (
          <button
            type="button"
            className="josh-e2e__button josh-e2e__button--secondary"
            onClick={loadStatus}
            disabled={!serviceDate || Boolean(busyAction)}
          >
            {busyAction === "status" ? "Refreshing…" : "Refresh Status"}
          </button>
        )}
      </section>

      <RequestError error={requestError} />

      {readinessCurrent && (
        <>
          <Readiness testState={testState} />
          <TestResults testState={testState} />

          <section className="josh-e2e__send-card" aria-label="Send Josh test push">
            <div>
              <h2>Send the real test notification</h2>
              <p>
                The web app asks the protected backend to send this push. It
                never contacts Expo directly.
              </p>
              {!deviceReady && (
                <p className="josh-e2e__inline-warning">
                  Sending is disabled until Josh's test device is ready.
                </p>
              )}
              {notificationSent && (
                <p className="josh-e2e__inline-success">
                  A notification has already been sent for this date. Use Refresh
                  Status after Josh completes the flow.
                </p>
              )}
            </div>
            <button
              type="button"
              className="josh-e2e__button josh-e2e__button--danger"
              onClick={() => setConfirmationOpen(true)}
              disabled={sendDisabled}
            >
              {notificationSent ? "Test Push Already Sent" : "Send Test Push to Josh"}
            </button>
          </section>
        </>
      )}

      {confirmationOpen && (
        <ConfirmationModal
          serviceDate={serviceDate}
          busy={busyAction === "send"}
          onCancel={() => setConfirmationOpen(false)}
          onConfirm={sendTest}
        />
      )}
    </main>
  );
};

export default JoshLessonConfirmationTest;
